import { FC, FormEvent, useCallback, useEffect, useRef } from 'react';

import { useInitializeTranslations } from '@telialt/common-cms/src/useTranslations';
import { RequestStatus } from '@telialt/models/src/RequestModel';
import { SegmentType } from '@telialt/models/src/SegmentType';
import { useHybrisSegment } from '@telialt/utils/src/segment';
import { useUserData } from '@telialt/utils/src/SSO/useUserData';

import {
    INPUT_NAMES,
    LEAD_DEFAULT_SERVICE_TYPE,
    LEAD_NAME_MIN_LENGTH,
    LEAD_NAME_PATTERN,
    LEAD_PHONE_MOBILE_PATTERN,
} from '../../constants';
import { useGAEvent } from '../../hooks/useGAEvent';
import { useLeadSubmit } from '../../hooks/useLeadSubmit';
import { LeadFormProps, LeadSmsConfirmRequestData, LeadSubmitRequestData } from '../../model';
import { getLeadSmsConfirmData, getLeadSubmitData, getLegaltext, mapUserDataToSubmitData } from '../../utils';
import { LeadError } from '../LeadError';
import { LeadFormSkeleton } from '../LeadFormSkeleton';
import { LeadInfo } from '../LeadInfo';
import { LeadSMSConfirm } from '../LeadSMSConfirm';
import { LeadSuccess } from '../LeadSuccess';

export const LeadForm: FC<LeadFormProps> = ({
    possibilityComment,
    addressText,
    addressId,
    leadText,
    leadsType,
    serviceType = LEAD_DEFAULT_SERVICE_TYPE,
    helpText,
    helpTooltip,
    responseTimeText,
    smsConfirmDescription,
    successExitLink,
    successExitLinkText,
    gaEventEcommerce,
    gaTracking,
    onSubmit,
    ...props
}) => {
    const { t } = useInitializeTranslations();
    const { userData } = useUserData();
    const segment = useHybrisSegment();
    const lastSubmitDataRef = useRef<LeadSubmitRequestData>(mapUserDataToSubmitData(userData, props.prefilledComment));
    const lastSmsConfirmDataRef = useRef<LeadSmsConfirmRequestData>(null);

    const { mutateSubmit, mutateSmsConfirm, resetSubmit, resetSmsConfirm, status, isSmsConfirm, isInvalidSmsConfirm } =
        useLeadSubmit(onSubmit);

    const displayHelpText = helpText || t?.['lead.default.help_text'] || '';
    const displayHelpTooltip = helpTooltip || t?.['lead.default.help_tooltip'] || '';

    const { isLoading, pushGAEvent } = useGAEvent(gaTracking, gaEventEcommerce);

    useEffect(() => {
        if (!isLoading) {
            pushGAEvent('formOpen');
        }
    }, [isLoading, pushGAEvent]);

    const submitHandler = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!(event.target instanceof HTMLFormElement) || !event.target.checkValidity()) {
                return;
            }

            const form = event.target;
            const formData = new FormData(form);
            const nameInputValue = formData.get(INPUT_NAMES.NAME) as string;
            const companyInputValue = formData.get(INPUT_NAMES.COMPANY_CODE) as string;
            const phoneInputValue = formData.get(INPUT_NAMES.PHONE_NUMBER) as string;

            if (
                segment === SegmentType.B2c &&
                nameInputValue &&
                (!LEAD_NAME_PATTERN.test(nameInputValue) || nameInputValue.length < LEAD_NAME_MIN_LENGTH)
            ) {
                const nameInput = form.elements.namedItem(INPUT_NAMES.NAME) as HTMLInputElement;
                nameInput.setCustomValidity(t?.['lead.message.name_error'] || '');
                form.checkValidity();

                return;
            }

            if (segment === SegmentType.B2b && !companyInputValue) {
                const companyInput = form.elements.namedItem(INPUT_NAMES.COMPANY_CODE) as HTMLInputElement;
                companyInput.setCustomValidity(t?.['lead.message.company_name_error'] || '');
                form.checkValidity();

                return;
            }

            if (!phoneInputValue || !LEAD_PHONE_MOBILE_PATTERN.test(phoneInputValue)) {
                const phoneInput = form.elements.namedItem(INPUT_NAMES.PHONE_NUMBER) as HTMLInputElement;
                phoneInput.setCustomValidity(t?.['lead.message.phone_error'] || '');
                form.checkValidity();

                return;
            }

            const legalText = getLegaltext(displayHelpText, displayHelpTooltip);

            const leadData = getLeadSubmitData({
                form,
                leadText,
                leadsType,
                serviceType,
                legalText,
                possibilityComment,
                addressText,
                addressId,
            });
            lastSubmitDataRef.current = leadData;

            mutateSubmit(leadData);

            pushGAEvent('formSubmissionSuccess', { phone: phoneInputValue });
        },
        [
            displayHelpText,
            displayHelpTooltip,
            leadText,
            mutateSubmit,
            serviceType,
            leadsType,
            possibilityComment,
            addressText,
            addressId,
            pushGAEvent,
            segment,
            t,
        ],
    );

    const smsConfirmHandler = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!(event.target instanceof HTMLFormElement)) {
                return;
            }

            const form = event.target;

            if (!form.checkValidity()) {
                return;
            }

            const legalText = getLegaltext(displayHelpText, displayHelpTooltip);

            const confirmData = getLeadSmsConfirmData({
                form,
                leadText,
                serviceType,
                legalText,
            });
            lastSmsConfirmDataRef.current = confirmData;

            mutateSmsConfirm(confirmData);
        },
        [displayHelpText, displayHelpTooltip, leadText, mutateSmsConfirm, serviceType],
    );

    const tryAgainHandler = useCallback(() => {
        resetSmsConfirm();
        if (!isSmsConfirm) {
            resetSubmit();
        }
    }, [isSmsConfirm, resetSmsConfirm, resetSubmit]);

    if (!t) {
        return <LeadFormSkeleton />;
    }

    if (status === RequestStatus.Success) {
        return (
            <LeadSuccess
                responseTimeText={responseTimeText}
                successExitLink={successExitLink}
                successExitLinkText={successExitLinkText}
            />
        );
    }

    if (status === RequestStatus.Error) {
        return <LeadError isInvalidSmsConfirm={!!isInvalidSmsConfirm} onTryAgain={tryAgainHandler} />;
    }

    if (isSmsConfirm) {
        return (
            <LeadSMSConfirm
                smsConfirmDescription={smsConfirmDescription}
                isLoading={status === RequestStatus.Pending}
                initialData={lastSmsConfirmDataRef.current || undefined}
                onSubmit={smsConfirmHandler}
            />
        );
    }

    return (
        <LeadInfo
            {...props}
            gaEventEcommerce={gaEventEcommerce}
            gaTracking={gaTracking}
            helpText={displayHelpText}
            helpTooltip={displayHelpTooltip}
            isLoading={status === RequestStatus.Pending}
            initialData={lastSubmitDataRef.current}
            onSubmit={submitHandler}
        />
    );
};
